import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

/**
 * The assistant's answers are markdown — it reaches for a table whenever it compares
 * anything, which is most of the time. Rendered as plain text those arrive as rows of
 * pipes and literal asterisks, directly over the numbers the reader most needs.
 *
 * Styling is per element rather than via a prose plugin so a table inside a chat bubble
 * stays compact and scrolls instead of overflowing the column — which matters more in the
 * docked pane, where the column is narrow.
 */
export default function Markdown({ text }: { text: string }) {
  return (
    <div className="break-words text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
          ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-0.5 last:mb-0">{children}</ul>,
          ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-0.5 last:mb-0">{children}</ol>,
          li: ({ children }) => <li className="pl-0.5">{children}</li>,
          h1: ({ children }) => <div className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</div>,
          h2: ({ children }) => <div className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</div>,
          h3: ({ children }) => <div className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</div>,
          code: ({ children }) => (
            <code className="rounded bg-gray-200/70 px-1 py-0.5 font-mono text-[12px]">{children}</code>
          ),
          pre: ({ children }) => (
            <pre className="mb-2 overflow-x-auto rounded bg-gray-100 p-2 text-[12px]">{children}</pre>
          ),
          table: ({ children }) => (
            <div className="mb-2 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300 text-[12px]">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
          th: ({ children }) => (
            <th className="whitespace-nowrap px-2 py-1 text-left font-medium text-gray-600">{children}</th>
          ),
          td: ({ children }) => <td className="whitespace-nowrap px-2 py-1 text-gray-800">{children}</td>,
          tr: ({ children }) => (
            <tr className="divide-x divide-gray-200 border-b border-gray-200 last:border-0">{children}</tr>
          ),
          a: ({ href, children }) => (
            <a href={href} className="text-blue-600 underline" target="_blank" rel="noreferrer">{children}</a>
          ),
          hr: () => <hr className="my-2 border-gray-200" />,
        }}
      >{text}</ReactMarkdown>
    </div>
  )
}
